<template>
  <v-container class="refurbish-container">
    <!-- ========================================
         KEINE BERECHTIGUNG WARNING
         ======================================== -->
    <v-alert
      v-if="!userRole"
      type="warning"
      variant="tonal"
      prominent
      border="start"
      class="mb-4"
    >
      <v-alert-title class="text-h6">
        <v-icon start>mdi-shield-alert</v-icon>
        Keine Berechtigung
      </v-alert-title>
      <div class="mt-2">
        Sie benötigen eine der folgenden Rollen für diese Seite:
        <ul class="mt-2">
          <li><strong>Refurbish:</strong> Paletten erstellen und Artikel scannen</li>
          <li><strong>Office:</strong> Paletten prüfen und freigeben</li>
          <li><strong>Versand:</strong> Finale Listen erstellen und versenden</li>
        </ul>
        <div class="mt-4">
          <v-btn color="primary" to="/dashboard" prepend-icon="mdi-arrow-left">
            Zurück zum Dashboard
          </v-btn>
        </div>
      </div>
    </v-alert>

    <!-- ========================================
         HAUPTANSICHT (nur mit Berechtigung)
         ======================================== -->
    <template v-if="userRole">
      <v-tabs v-model="mainTab" class="mb-4" color="orange-darken-1" density="comfortable">
        <v-tab value="paletten">
          <v-icon start size="18">mdi-package-variant</v-icon>
          Frankreich Paletten
        </v-tab>
        <v-tab
          v-if="hasRefurbishRole || authStore.user?.admin || authStore.user?.super_admin || authStore.user?.developer"
          value="artikelscan"
        >
          <v-icon start size="18">mdi-barcode-scan</v-icon>
          Artikelscan
        </v-tab>
      </v-tabs>

      <RefurbishArtikelscanTab v-if="mainTab === 'artikelscan'" />

      <template v-if="mainTab === 'paletten'">
      <!-- ========================================
           TOOLBAR & NAVIGATION
           ======================================== -->
      <v-toolbar color="transparent" class="mb-4">
        <v-toolbar-title class="text-h5">
          <v-icon class="mr-2" color="orange-darken-1">mdi-tools</v-icon>
          Frankreich Palettenverwaltung
        </v-toolbar-title>
        <v-spacer></v-spacer>

        <!-- Statistik-Chips -->
        <v-chip class="mr-2" color="info" variant="tonal">
          <v-icon start>mdi-cube-outline</v-icon>
          {{ statistik.meine_offenen_paletten || 0 }} offene
        </v-chip>
        <v-chip class="mr-4" color="success" variant="tonal">
          <v-icon start>mdi-barcode-scan</v-icon>
          {{ statistik.heute_gescannt || 0 }} heute
        </v-chip>

        <v-btn
          v-if="hasRefurbishRole && !scannerAktiv"
          color="orange-darken-1"
          prepend-icon="mdi-barcode-scan"
          @click="aktiviereScanner"
          elevation="2"
        >
          Artikel erfassen
        </v-btn>
        <v-btn
          v-if="hasRefurbishRole && scannerAktiv"
          color="warning"
          prepend-icon="mdi-stop"
          @click="deaktiviereScanner"
          elevation="2"
        >
          Scanner beenden
        </v-btn>
      </v-toolbar>

    <!-- ========================================
         SCANNER-ANSICHT (Artikel erfassen)
         ======================================== -->
    <v-row v-if="scannerAktiv && hasRefurbishRole">
      <!-- Sidebar: Offene Paletten -->
      <v-col cols="12" md="3">
        <v-card class="status-overview-card">
          <v-card-title class="d-flex align-center">
            <v-icon color="primary" size="24" class="mr-2">mdi-package-variant-multiple</v-icon>
            Offene Paletten
          </v-card-title>
          <v-divider class="card-divider"></v-divider>
          <v-card-text class="pa-0">
            <v-list density="compact">
              <v-list-item
                v-for="palette in offenePaletten"
                :key="palette.id"
                @click="loadPalette(palette.id)"
                :active="selectedPalette?.id === palette.id"
              >
                <template v-slot:prepend>
                  <v-icon color="orange-darken-1">mdi-package</v-icon>
                </template>
                <v-list-item-title class="font-weight-bold">
                  Palette {{ palette.arbeits_paletten_nr }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  {{ palette.anzahl_positionen }} Artikel · {{ palette.gesamtstueckzahl }} Stück
                </v-list-item-subtitle>
                <template v-slot:append>
                  <v-chip size="x-small" color="success">
                    {{ parseFloat(palette.gesamtgewicht) ? parseFloat(palette.gesamtgewicht).toFixed(1) + ' kg' : '0 kg' }}
                  </v-chip>
                </template>
              </v-list-item>
              <v-list-item v-if="offenePaletten.length === 0">
                <v-list-item-title class="text-grey text-center">
                  Keine offenen Paletten
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Scanner-Bereich -->
      <v-col cols="12" md="9">
        <v-card class="status-overview-card mb-4">
          <v-card-title class="d-flex align-center">
            <v-icon color="primary" size="24" class="mr-2">mdi-barcode-scan</v-icon>
            Artikel scannen
            <v-spacer></v-spacer>
            <v-chip color="success" size="small">
              Automatische Paletten-Zuordnung
            </v-chip>
          </v-card-title>
          <v-divider class="card-divider"></v-divider>
          <v-card-text class="py-4">
            <v-row align="center">
              <v-col cols="12" md="2">
                <v-select
                  v-model="searchType"
                  :items="[
                    { value: 'ean', title: 'EAN Scannen' },
                    { value: 'model', title: 'Modell eingeben' },
                    { value: 'cmmf', title: 'CMMF eingeben' }
                  ]"
                  label="Suchmodus"
                  variant="outlined"
                  bg-color="rgba(30, 30, 40, 0.5)"
                  hide-details="auto"
                  class="form-field"
                  color="success"
                  density="comfortable"
                >
                  <template v-slot:prepend-inner>
                    <v-icon color="success" size="small">{{ searchType === 'ean' ? 'mdi-barcode' : searchType === 'model' ? 'mdi-tag-text' : 'mdi-alpha-c-box' }}</v-icon>
                  </template>
                </v-select>
              </v-col>
              <v-col cols="12" md="8">
                <!-- EAN Scanner Modus -->
                <v-text-field
                  v-if="searchType === 'ean'"
                  ref="scannerInput"
                  v-model="scanInput"
                  label="EAN/GTIN Code scannen"
                  variant="outlined"
                  bg-color="rgba(30, 30, 40, 0.5)"
                  hide-details="auto"
                  class="form-field"
                  color="success"
                  :loading="loading.scanning"
                  :disabled="loading.scanning"
                  autofocus
                  @keydown.enter="handleScan"
                  @input="autoScanOnEanComplete"
                  hint="System ordnet automatisch der richtigen Palette zu"
                  persistent-hint
                >
                  <template v-slot:prepend-inner>
                    <v-icon color="success" size="small">mdi-barcode-scan</v-icon>
                  </template>
                  <template v-slot:append>
                    <v-btn
                      icon="mdi-qrcode-scan"
                      color="orange-darken-1"
                      @click="handleScan"
                      :loading="loading.scanning"
                    ></v-btn>
                  </template>
                </v-text-field>

                <!-- Code Commercial Autocomplete Modus -->
                <v-autocomplete
                  v-else-if="searchType === 'model'"
                  ref="scannerInput"
                  v-model="scanInput"
                  :items="codeCommercialOptions"
                  item-title="display"
                  item-value="code_commercial"
                  label="Code Commercial (Modell) eingeben"
                  variant="outlined"
                  bg-color="rgba(30, 30, 40, 0.5)"
                  hide-details="auto"
                  class="form-field"
                  color="success"
                  :loading="loading.scanning || loading.artikel"
                  :disabled="loading.scanning"
                  autofocus
                  @keydown.enter="handleScan"
                  hint="Suche nach Modellnummer (z.B. ARCR02)"
                  persistent-hint
                  clearable
                >
                  <template v-slot:prepend-inner>
                    <v-icon color="success" size="small">mdi-magnify</v-icon>
                  </template>
                  <template v-slot:append>
                    <v-btn
                      icon="mdi-magnify"
                      color="orange-darken-1"
                      @click="handleScan"
                      :loading="loading.scanning"
                    ></v-btn>
                  </template>
                </v-autocomplete>

                <!-- CMMF Autocomplete Modus -->
                <v-autocomplete
                  v-else-if="searchType === 'cmmf'"
                  ref="scannerInput"
                  v-model="scanInput"
                  :items="cmmfOptions"
                  item-title="display"
                  item-value="code_cmmf"
                  label="CMMF-Nummer eingeben"
                  variant="outlined"
                  bg-color="rgba(30, 30, 40, 0.5)"
                  hide-details="auto"
                  class="form-field"
                  color="success"
                  :loading="loading.scanning || loading.artikel"
                  :disabled="loading.scanning"
                  autofocus
                  @keydown.enter="handleScan"
                  hint="Suche nach CMMF-Nummer"
                  persistent-hint
                  clearable
                >
                  <template v-slot:prepend-inner>
                    <v-icon color="success" size="small">mdi-magnify</v-icon>
                  </template>
                  <template v-slot:append>
                    <v-btn
                      icon="mdi-magnify"
                      color="orange-darken-1"
                      @click="handleScan"
                      :loading="loading.scanning"
                    ></v-btn>
                  </template>
                </v-autocomplete>
              </v-col>
              <v-col cols="12" md="2">
                <v-text-field
                  v-model.number="scanMenge"
                  label="Menge"
                  type="number"
                  min="1"
                  variant="outlined"
                  density="comfortable"
                  :disabled="loading.scanning"
                ></v-text-field>
              </v-col>
            </v-row>

            <!-- Scan-Feedback -->
            <v-alert
              v-if="scanFeedback.show"
              :type="scanFeedback.type"
              variant="tonal"
              density="compact"
              closable
              @click:close="scanFeedback.show = false"
              class="mt-2"
            >
              {{ scanFeedback.message }}
            </v-alert>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- ========================================
         PALETTEN-LISTE (Übersicht)
         ======================================== -->
    <v-card v-if="!selectedPalette && !scannerAktiv" class="mb-4 overview-card">
      <v-card-title class="d-flex align-center">
        <v-icon class="mr-2">mdi-format-list-bulleted</v-icon>
        <span v-if="userRole === 'refurbish'">Offene Paletten</span>
        <span v-else-if="userRole === 'office'">Office - Zu bearbeitende Paletten</span>
        <span v-else>Versand - Finale Listen</span>
      </v-card-title>
      <v-divider></v-divider>

      <!-- Tabs: Aktiv / Archiv -->
      <v-tabs v-model="viewTab" bg-color="grey-darken-4">
        <v-tab value="aktiv">
          <v-icon start>mdi-clock-outline</v-icon>
          Aktive Paletten
        </v-tab>
        <v-tab value="archiv">
          <v-icon start>mdi-archive</v-icon>
          Archiv
        </v-tab>
      </v-tabs>

      <v-card-text>
        <!-- Status-Filter -->
        <div class="mb-4">
          <v-chip-group v-model="statusFilter" mandatory>
            <v-chip value="alle" filter>Alle</v-chip>
            <v-chip v-if="viewTab === 'aktiv' && userRole === 'refurbish'" value="erfassung" filter color="orange">
              Erfassung
            </v-chip>
            <v-chip v-if="viewTab === 'aktiv' && userRole === 'office'" value="office_freigabe" filter color="indigo">
              Office
            </v-chip>
            <v-chip v-if="viewTab === 'aktiv' && userRole === 'versand'" value="versand_final" filter color="blue">
              Versand
            </v-chip>
            <v-chip v-if="viewTab === 'archiv'" value="abgeschlossen" filter color="green">
              Abgeschlossen
            </v-chip>
          </v-chip-group>
        </div>

        <v-data-table
          :headers="palettenTableHeaders"
          :items="filteredPaletten"
          :loading="loading.paletten"
          item-value="id"
          class="elevation-0"
        >
          <template v-slot:[`item.paletten_nr`]="{ item }">
            <strong class="text-orange-darken-1">{{ item.paletten_nr }}</strong>
          </template>
          <template v-slot:[`item.arbeits_paletten_nr`]="{ item }">
            <v-chip size="small" color="orange" variant="outlined">
              <v-icon start size="small">mdi-sort-numeric-variant</v-icon>
              {{ item.arbeits_paletten_nr || '—' }}
            </v-chip>
          </template>
          <template v-slot:[`item.erstellt_von_name`]="{ item }">
            {{ item.erstellt_von_name || '—' }}
          </template>
          <template v-slot:[`item.erfassung_datum`]="{ item }">
            {{ formatDateTime(item.erfassung_datum) }}
          </template>
          <template v-slot:[`item.anzahl_positionen`]="{ item }">
            <v-chip size="small" color="info">
              <v-icon start size="small">mdi-package-variant</v-icon>
              {{ item.anzahl_positionen }}
            </v-chip>
          </template>
          <template v-slot:[`item.gesamtstueckzahl`]="{ item }">
            <v-chip size="small" color="success">
              <v-icon start size="small">mdi-counter</v-icon>
              {{ item.gesamtstueckzahl || 0 }}
            </v-chip>
          </template>
          <template v-slot:[`item.gesamtgewicht`]="{ item }">
            <v-chip size="small" color="orange-darken-1">
              <v-icon start size="small">mdi-weight-kilogram</v-icon>
              {{ parseFloat(item.gesamtgewicht) ? parseFloat(item.gesamtgewicht).toFixed(2) + ' kg' : '—' }}
            </v-chip>
          </template>
          <template v-slot:[`item.status`]="{ item }">
            <v-chip
              :color="getStatusColor(item.status)"
              size="small"
              variant="flat"
            >
              <v-icon start size="small">{{ getStatusIcon(item.status) }}</v-icon>
              {{ getStatusText(item.status, item) }}
            </v-chip>
          </template>
          <template v-slot:[`item.actions`]="{ item }">
            <v-btn
              icon="mdi-eye"
              size="small"
              variant="text"
              color="primary"
              @click="loadPalette(item.id)"
              title="Öffnen"
            ></v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- ========================================
         DETAIL-ANSICHT (Paletten-Bearbeitung)
         ======================================== -->
    <div v-if="selectedPalette">
      <!-- Zurück-Button -->
      <v-btn
        class="mb-4"
        prepend-icon="mdi-arrow-left"
        @click="backToList"
      >
        Zurück zur Übersicht
      </v-btn>

      <!-- Paletten-Informationen -->
      <v-card class="mb-4 palette-info-card">
        <v-card-title class="d-flex align-center">
          <v-icon class="mr-2" color="orange-darken-1">mdi-cube-outline</v-icon>
          Palette: {{ selectedPalette.paletten_nr }}
          <v-spacer></v-spacer>
          <v-chip
            :color="getStatusColor(selectedPalette.status)"
            variant="flat"
            size="large"
          >
            <v-icon start>{{ getStatusIcon(selectedPalette.status) }}</v-icon>
            {{ getStatusText(selectedPalette.status, selectedPalette) }}
          </v-chip>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <v-row>
            <!-- Workflow-Informationen -->
            <v-col cols="12" md="4">
              <div class="info-box">
                <div class="info-label">
                  <v-icon size="small" class="mr-1">mdi-account</v-icon>
                  Erstellt von
                </div>
                <div class="info-value">
                  {{ selectedPalette.erstellt_von_vorname }} {{ selectedPalette.erstellt_von_nachname }}
                  ({{ selectedPalette.erstellt_von_name }})
                </div>
                <div class="info-subtext">
                  {{ formatDateTime(selectedPalette.erfassung_datum) }}
                </div>
              </div>
            </v-col>

            <v-col cols="12" md="4" v-if="selectedPalette.office_bearbeiter_name">
              <div class="info-box">
                <div class="info-label">
                  <v-icon size="small" class="mr-1" color="indigo">mdi-briefcase</v-icon>
                  Office bearbeitet
                </div>
                <div class="info-value">{{ selectedPalette.office_bearbeiter_name }}</div>
                <div class="info-subtext" v-if="selectedPalette.office_freigabe_datum">
                  {{ formatDateTime(selectedPalette.office_freigabe_datum) }}
                </div>
              </div>
            </v-col>

            <v-col cols="12" md="4" v-if="selectedPalette.versand_bearbeiter_name">
              <div class="info-box">
                <div class="info-label">
                  <v-icon size="small" class="mr-1" color="blue">mdi-truck-check</v-icon>
                  Versand finalisiert
                </div>
                <div class="info-value">{{ selectedPalette.versand_bearbeiter_name }}</div>
                <div class="info-subtext" v-if="selectedPalette.versand_abschluss_datum">
                  {{ formatDateTime(selectedPalette.versand_abschluss_datum) }}
                </div>
              </div>
            </v-col>

            <!-- Statistik -->
            <v-col cols="12" md="6">
              <v-alert type="info" variant="tonal" density="compact" class="mb-0">
                <v-row align="center">
                  <v-col cols="auto">
                    <v-icon size="large">mdi-package-variant</v-icon>
                  </v-col>
                  <v-col>
                    <strong>{{ selectedPalette.anzahl_positionen }} Artikel</strong> auf dieser Palette
                  </v-col>
                </v-row>
              </v-alert>
            </v-col>
            <v-col cols="12" md="6">
              <v-alert type="success" variant="tonal" density="compact" class="mb-0">
                <v-row align="center">
                  <v-col cols="auto">
                    <v-icon size="large">mdi-counter</v-icon>
                  </v-col>
                  <v-col>
                    <strong>{{ gesamtStueckzahl }} Stück</strong> gesamt
                  </v-col>
                </v-row>
              </v-alert>
            </v-col>
            <v-col cols="12" md="6">
              <v-alert type="warning" variant="tonal" density="compact" class="mb-0">
                <v-row align="center">
                  <v-col cols="auto">
                    <v-icon size="large">mdi-weight-kilogram</v-icon>
                  </v-col>
                  <v-col>
                    <strong>{{ parseFloat(selectedPalette.gesamtgewicht) ? parseFloat(selectedPalette.gesamtgewicht).toFixed(2) + ' kg' : 'Kein Gewicht' }}</strong> Gesamtgewicht
                  </v-col>
                </v-row>
              </v-alert>
            </v-col>

            <!-- Notizen -->
            <v-col cols="12" v-if="hasRefurbishRole && selectedPalette.status === 'erfassung'">
              <v-textarea
                v-model="selectedPalette.refurbish_notiz"
                label="Notizen"
                variant="outlined"
                rows="2"
                density="comfortable"
                prepend-inner-icon="mdi-note-text"
                @blur="saveNote('refurbish_notiz')"
              ></v-textarea>
            </v-col>

            <v-col cols="12" v-if="selectedPalette.status === 'office_freigabe'">
              <v-textarea
                v-model="selectedPalette.office_notiz"
                label="Notizen (Office)"
                variant="outlined"
                rows="2"
                density="comfortable"
                prepend-inner-icon="mdi-note-text"
                color="indigo"
                @blur="saveNote('office_notiz')"
              ></v-textarea>
            </v-col>

            <v-col cols="12" v-if="selectedPalette.office_notiz && selectedPalette.status !== 'office_freigabe'">
              <v-textarea
                :model-value="selectedPalette.office_notiz"
                label="Notizen vom Office"
                variant="outlined"
                rows="2"
                density="comfortable"
                prepend-inner-icon="mdi-information-outline"
                color="indigo"
                readonly
                bg-color="grey-lighten-4"
              ></v-textarea>
            </v-col>

            <v-col cols="12" v-if="selectedPalette.status === 'versand_final'">
              <v-textarea
                v-model="selectedPalette.versand_notiz"
                label="Notizen (Versand)"
                variant="outlined"
                rows="2"
                density="comfortable"
                prepend-inner-icon="mdi-note-text"
                color="blue"
                @blur="saveNote('versand_notiz')"
              ></v-textarea>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- ========================================
           EAN SCANNER (nur in Erfassung)
           ======================================== -->
      <v-card v-if="selectedPalette.status === 'erfassung' && hasRefurbishRole" class="mb-4 scanner-card">
        <v-card-title class="d-flex align-center">
          <v-icon class="mr-2" color="orange-darken-1">mdi-barcode-scan</v-icon>
          Artikel scannen
          <v-spacer></v-spacer>
          <v-chip color="success" variant="tonal">
            <v-icon start size="small">mdi-lightning-bolt</v-icon>
            Schnellerfassung
          </v-chip>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <v-row align="center">
            <v-col cols="12" md="10">
              <v-text-field
                ref="scannerDetailInput"
                v-model="scanInput"
                label="EAN/GTIN Code scannen oder eingeben"
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-barcode"
                :loading="loading.scanning"
                :disabled="loading.scanning"
                autofocus
                @keydown.enter="handleScan"
                hint="Scannen Sie einfach hintereinander alle Artikel"
                persistent-hint
              >
                <template v-slot:append>
                  <v-btn
                    icon="mdi-qrcode-scan"
                    color="orange-darken-1"
                    @click="handleScan"
                    :loading="loading.scanning"
                  ></v-btn>
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="12" md="2">
              <v-text-field
                v-model.number="scanMenge"
                label="Menge"
                type="number"
                min="1"
                variant="outlined"
                density="comfortable"
                :disabled="loading.scanning"
              ></v-text-field>
            </v-col>
          </v-row>

          <!-- Scan-Feedback -->
          <v-alert
            v-if="scanFeedback.show"
            :type="scanFeedback.type"
            variant="tonal"
            density="compact"
            closable
            @click:close="scanFeedback.show = false"
            class="mt-2"
          >
            {{ scanFeedback.message }}
          </v-alert>
        </v-card-text>
      </v-card>

      <!-- ========================================
           ARTIKEL-LISTE
           ======================================== -->
      <v-card class="mb-4 positions-card">
        <v-card-title class="d-flex align-center">
          <v-icon class="mr-2">mdi-format-list-checkbox</v-icon>
          Artikel auf der Palette
          <v-spacer></v-spacer>
          <v-chip color="info" variant="tonal">
            {{ positionen.length }} Artikel
          </v-chip>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <v-data-table
            :headers="positionenTableHeaders"
            :items="positionen"
            :loading="loading.positionen"
            item-value="id"
            class="elevation-0"
            density="comfortable"
          >
            <template v-slot:[`item.reihenfolge`]="{ item }">
              <v-chip size="x-small" color="grey">{{ item.reihenfolge }}</v-chip>
            </template>
            <template v-slot:[`item.gtin_ean`]="{ item }">
              <div class="d-flex align-center">
                <v-icon size="small" class="mr-1">mdi-barcode</v-icon>
                <code>{{ item.gtin_ean }}</code>
              </div>
            </template>
            <template v-slot:[`item.code_cmmf`]="{ item }">
              <strong>{{ item.code_cmmf || '—' }}</strong>
            </template>
            <template v-slot:[`item.description_cmmf`]="{ item }">
              {{ item.description_cmmf || '—' }}
            </template>
            <template v-slot:[`item.menge`]="{ item }">
              <v-chip size="small" color="primary">{{ item.menge }}x</v-chip>
            </template>
            <template v-slot:[`item.gewicht_kg`]="{ item }">
              <div class="text-body-2">
                {{ parseFloat(item.gewicht_kg) ? parseFloat(item.gewicht_kg).toFixed(3) + ' kg' : '—' }}
              </div>
              <div class="text-caption text-success" v-if="parseFloat(item.gewicht_kg) && item.menge">
                ({{ (parseFloat(item.gewicht_kg) * item.menge).toFixed(2) }} kg)
              </div>
            </template>
            <template v-slot:[`item.erfasst_datum`]="{ item }">
              <div class="text-caption">
                {{ formatTime(item.erfasst_datum) }}
              </div>
            </template>
            <template v-slot:[`item.actions`]="{ item }">
              <v-btn
                v-if="selectedPalette.status === 'erfassung' && hasRefurbishRole"
                icon="mdi-delete"
                size="small"
                variant="text"
                color="error"
                @click="removePosition(item.id)"
                title="Entfernen"
              ></v-btn>
            </template>

            <template v-slot:no-data>
              <div class="text-center pa-8">
                <v-icon size="64" color="grey">mdi-package-variant-closed-remove</v-icon>
                <p class="text-h6 mt-4">Noch keine Artikel gescannt</p>
                <p class="text-caption">Scannen Sie Artikel mit dem EAN-Scanner</p>
              </div>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>

      <!-- ========================================
           AKTIONEN
           ======================================== -->
      <v-card class="actions-card">
        <v-card-text>
          <v-row>
            <!-- Refurbish: Freigabe an Office -->
            <v-col cols="12" v-if="hasRefurbishRole && selectedPalette.status === 'erfassung'">
              <v-btn
                block
                color="orange-darken-1"
                size="large"
                prepend-icon="mdi-check-circle"
                @click="confirmFreigabeOffice"
                :disabled="positionen.length === 0"
              >
                Palette abschließen und an Office freigeben
              </v-btn>
            </v-col>

            <!-- Office: Freigabe an Versand oder zurück -->
            <v-col cols="12" md="6" v-if="selectedPalette.status === 'office_freigabe'">
              <v-btn
                block
                color="indigo-darken-1"
                size="large"
                prepend-icon="mdi-arrow-right"
                @click="updateStatus('versand_final')"
              >
                An Versand freigeben
              </v-btn>
            </v-col>
            <v-col cols="12" md="6" v-if="selectedPalette.status === 'office_freigabe'">
              <v-btn
                block
                color="warning"
                size="large"
                variant="outlined"
                prepend-icon="mdi-arrow-left"
                @click="updateStatus('erfassung')"
              >
                Zurück an Refurbish
              </v-btn>
            </v-col>

            <!-- Versand: Abschließen oder zurück -->
            <v-col cols="12" md="6" v-if="selectedPalette.status === 'versand_final'">
              <v-btn
                block
                color="green"
                size="large"
                prepend-icon="mdi-check-all"
                @click="updateStatus('abgeschlossen')"
              >
                Palette abschließen
              </v-btn>
            </v-col>
            <v-col cols="12" md="6" v-if="selectedPalette.status === 'versand_final'">
              <v-btn
                block
                color="warning"
                size="large"
                variant="outlined"
                prepend-icon="mdi-arrow-left"
                @click="updateStatus('office_freigabe')"
              >
                Zurück an Office
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </div>

    <!-- ========================================
         BESTÄTIGUNGS-DIALOGE
         ======================================== -->

    <!-- Artikel Scan Bestätigung -->
    <v-dialog
      v-model="dialogs.confirmScan"
      max-width="500"
      persistent
      @keydown.enter="confirmAndScan"
      @keydown.esc="cancelScan"
    >
      <v-card>
        <v-card-title class="text-subtitle-1 d-flex align-center bg-orange-darken-1 text-white py-3">
          <v-icon class="mr-2" size="small" color="white">mdi-checkbox-marked-circle-outline</v-icon>
          Artikel bestätigen
        </v-card-title>
        <v-card-text class="pt-4 pb-2">
          <div v-if="confirmScanData.artikel" class="artikel-confirm-details">
            <v-row dense>
              <v-col cols="12">
                <div class="detail-box pa-3 mb-2" style="background: rgba(30, 30, 40, 0.3); border-radius: 6px;">
                  <div class="text-caption text-grey mb-1">EAN/GTIN</div>
                  <div class="text-subtitle-1 font-weight-bold text-monospace">{{ confirmScanData.artikel.gtin_ean }}</div>
                </div>
              </v-col>
              <v-col cols="12">
                <div class="detail-box pa-3 mb-2" style="background: rgba(30, 30, 40, 0.3); border-radius: 6px;">
                  <div class="text-caption text-grey mb-1">Code CMMF</div>
                  <div class="text-subtitle-2 font-weight-bold text-monospace">{{ confirmScanData.artikel.code_cmmf || '-' }}</div>
                </div>
              </v-col>
              <v-col cols="12">
                <div class="detail-box pa-3 mb-2" style="background: rgba(30, 30, 40, 0.3); border-radius: 6px;">
                  <div class="text-caption text-grey mb-1">Code Commercial (SKU)</div>
                  <div class="text-subtitle-2 font-weight-bold text-monospace" style="color: #42a5f5;">{{ confirmScanData.artikel.code_commercial || '-' }}</div>
                </div>
              </v-col>
              <v-col cols="12">
                <div class="detail-box pa-3 mb-2" style="background: rgba(30, 30, 40, 0.3); border-radius: 6px;">
                  <div class="text-caption text-grey mb-1">Produktbeschreibung</div>
                  <div class="text-body-2">{{ confirmScanData.artikel.description_cmmf || confirmScanData.artikel.code_commercial || '-' }}</div>
                </div>
              </v-col>
              <v-col cols="12">
                <div class="detail-box pa-3 mb-2" style="background: rgba(255, 152, 0, 0.1); border-radius: 6px; border: 1px solid rgba(255, 152, 0, 0.3);">
                  <div class="text-caption text-grey mb-1">Arbeitsnummer (Palette)</div>
                  <div class="text-h6 font-weight-bold text-orange-darken-1">{{ confirmScanData.artikel.paletten_nr || '-' }}</div>
                </div>
              </v-col>
              <v-col cols="6">
                <div class="detail-box pa-3" style="background: rgba(30, 30, 40, 0.3); border-radius: 6px;">
                  <div class="text-caption text-grey mb-1">Gewicht</div>
                  <div class="text-body-2 font-weight-bold">{{ confirmScanData.artikel.gewicht_kg || '0' }} kg</div>
                </div>
              </v-col>
              <v-col cols="6">
                <div class="detail-box pa-3" style="background: rgba(30, 30, 40, 0.3); border-radius: 6px;">
                  <div class="text-caption text-grey mb-1">Menge</div>
                  <div class="text-body-2 font-weight-bold text-success">{{ confirmScanData.menge }}x</div>
                </div>
              </v-col>
            </v-row>
          </div>
        </v-card-text>
        <v-card-actions class="pa-3">
          <v-spacer></v-spacer>
          <v-btn
            variant="text"
            @click="cancelScan"
            :disabled="loading.scanning"
            size="small"
          >
            <v-icon start size="small">mdi-close</v-icon>
            Abbrechen
          </v-btn>
          <v-btn
            color="success"
            variant="elevated"
            @click="confirmAndScan"
            :loading="loading.scanning"
            size="small"
          >
            <v-icon start size="small">mdi-check</v-icon>
            Bestätigen (Enter)
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Freigabe Office Bestätigung -->
    <v-dialog v-model="dialogs.freigabeOffice" max-width="500">
      <v-card>
        <v-card-title class="text-h6 d-flex align-center">
          <v-icon class="mr-2" color="warning">mdi-alert-circle</v-icon>
          Palette an Office freigeben?
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4">
          <v-alert type="info" variant="tonal" density="compact" class="mb-4">
            <strong>{{ positionen.length }} Artikel</strong> auf dieser Palette
          </v-alert>

          <div class="checklist">
            <div class="checklist-item">
              <v-icon color="success" class="mr-2">mdi-checkbox-marked-circle</v-icon>
              <span>Sind alle Artikel erfasst?</span>
            </div>
            <div class="checklist-item">
              <v-icon color="success" class="mr-2">mdi-checkbox-marked-circle</v-icon>
              <span>Sind alle Daten korrekt?</span>
            </div>
            <div class="checklist-item">
              <v-icon color="warning" class="mr-2">mdi-information</v-icon>
              <span>Nach der Freigabe können Sie die Palette nicht mehr bearbeiten</span>
            </div>
          </div>

          <v-alert type="warning" variant="tonal" density="compact" class="mt-4">
            <strong>Letzte Änderung:</strong> vor {{ getTimeAgo(selectedPalette?.aktualisierungsdatum) }}
          </v-alert>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="dialogs.freigabeOffice = false">
            Abbrechen
          </v-btn>
          <v-btn
            color="orange-darken-1"
            variant="elevated"
            @click="freigabeAnOffice"
            :loading="loading.status"
          >
            <v-icon start>mdi-check</v-icon>
            Freigeben
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Artikel-Auswahl bei doppelten EAN-Codes -->
    <v-dialog v-model="dialogs.selectArtikel" max-width="700">
      <v-card>
        <v-card-title class="text-h6 d-flex align-center">
          <v-icon class="mr-2" color="warning">mdi-alert-decagram</v-icon>
          Mehrere Artikel gefunden
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4">
          <v-alert type="info" variant="tonal" density="compact" class="mb-4">
            EAN <strong>{{ artikelAuswahl.ean }}</strong> ist mehrfach im System vorhanden.<br>
            Bitte wählen Sie den richtigen Artikel:
          </v-alert>

          <v-list lines="three" density="compact">
            <v-list-item
              v-for="artikel in artikelAuswahl.artikel"
              :key="artikel.id"
              @click="selectArtikelFromList(artikel)"
              class="artikel-auswahl-item"
              hover
            >
              <template v-slot:prepend>
                <v-avatar color="orange-darken-1" size="48">
                  <v-icon>mdi-barcode</v-icon>
                </v-avatar>
              </template>
              <v-list-item-title class="font-weight-bold">
                {{ artikel.code_cmmf }}
              </v-list-item-title>
              <v-list-item-subtitle>
                {{ artikel.description_cmmf }}
              </v-list-item-subtitle>
              <v-list-item-subtitle class="mt-1">
                <v-chip size="x-small" color="blue" class="mr-1">
                  Commercial: {{ artikel.code_commercial }}
                </v-chip>
                <v-chip size="x-small" color="purple" v-if="artikel.gtin_ean" class="mr-1">
                  EAN: {{ artikel.gtin_ean }}
                </v-chip>
                <v-chip size="x-small" color="green" v-if="artikel.gewicht_kg">
                  {{ artikel.gewicht_kg }} kg
                </v-chip>
              </v-list-item-subtitle>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="dialogs.selectArtikel = false; scanInput = ''; scannerInput?.focus();">
            Abbrechen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Mengenauswahl beim Löschen -->
    <v-dialog v-model="dialogs.removeQuantity" max-width="500">
      <v-card>
        <v-card-title class="text-h6 bg-orange-darken-1 text-white">
          <v-icon class="mr-2">mdi-package-variant-minus</v-icon>
          Artikel entfernen
        </v-card-title>
        <v-card-text class="pt-4">
          <div v-if="removeQuantityData.artikelInfo" class="mb-4">
            <div class="text-subtitle-2 text-grey-darken-1 mb-1">Artikel</div>
            <div class="font-weight-bold">{{ removeQuantityData.artikelInfo.code_cmmf }}</div>
            <div class="text-caption text-grey">{{ removeQuantityData.artikelInfo.description }}</div>
          </div>

          <v-alert type="info" variant="tonal" density="compact" class="mb-4">
            Aktuelle Menge: <strong>{{ removeQuantityData.currentMenge }} Stück</strong>
          </v-alert>

          <v-text-field
            v-model.number="removeQuantityData.removeMenge"
            label="Anzahl zum Entfernen"
            type="number"
            :min="1"
            :max="removeQuantityData.currentMenge"
            variant="outlined"
            density="comfortable"
            hint="Geben Sie die Anzahl ein, die entfernt werden soll"
            persistent-hint
            autofocus
          >
            <template v-slot:prepend-inner>
              <v-icon color="orange">mdi-numeric</v-icon>
            </template>
          </v-text-field>

          <div class="mt-3 text-caption text-grey">
            <v-icon size="small" class="mr-1">mdi-information-outline</v-icon>
            Verbleibend nach Entfernung: {{ removeQuantityData.currentMenge - removeQuantityData.removeMenge }} Stück
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn
            @click="dialogs.removeQuantity = false"
            variant="text"
          >
            Abbrechen
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="orange-darken-1"
            variant="flat"
            @click="confirmRemoveQuantity"
            :disabled="removeQuantityData.removeMenge < 1 || removeQuantityData.removeMenge > removeQuantityData.currentMenge"
          >
            <v-icon class="mr-1">mdi-check</v-icon>
            Entfernen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
      </template>
    </template>
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { useToast } from 'vue-toastification';
import axios from 'axios';
import { API_BASE_URL } from '@/config';
import RefurbishArtikelscanTab from '@/components/RefurbishArtikelscanTab.vue';

const authStore = useAuthStore();
const toast = useToast();

// ========================================
// STATE
// ========================================

const mainTab = ref('paletten');
const scannerAktiv = ref(false);
const offenePaletten = ref([]);
const paletten = ref([]);
const selectedPalette = ref(null);
const positionen = ref([]);
const statusFilter = ref('alle');
const viewTab = ref('aktiv'); // 'aktiv' oder 'archiv'
const scanInput = ref('');
const scanMenge = ref(1);
const searchType = ref('ean');
const artikelListe = ref([]);

const codeCommercialOptions = computed(() => {
  return artikelListe.value
    .filter(a => a.code_commercial && a.paletten_nr)
    .map(a => ({
      code_commercial: a.code_commercial,
      paletten_nr: a.paletten_nr,
      description: `${a.description_cmmf || a.code_cmmf || ''} (EAN: ${a.gtin_ean}) [CMMF: ${a.code_cmmf || '—'}]`,
      display: `${a.code_commercial} - ${a.description_cmmf || a.code_cmmf || ''} [CMMF: ${a.code_cmmf || '—'}]`
    }));
});

const cmmfOptions = computed(() => {
  return artikelListe.value
    .filter(a => a.code_cmmf && a.paletten_nr)
    .map(a => ({
      code_cmmf: a.code_cmmf,
      paletten_nr: a.paletten_nr,
      description: `${a.description_cmmf || a.code_cmmf} (EAN: ${a.gtin_ean})`,
      display: `${a.code_cmmf} - ${a.description_cmmf || a.code_cmmf}`
    }));
});

const scannerInput = ref(null);
const statistik = ref({
  meine_offenen_paletten: 0,
  heute_gescannt: 0
});

const loading = ref({
  paletten: false,
  positionen: false,
  scanning: false,
  status: false,
  artikel: false
});

const dialogs = ref({
  freigabeOffice: false,
  confirmScan: false,
  selectArtikel: false,  // Neu: Für Auswahl bei doppelten EAN-Codes
  removeQuantity: false  // Neu: Für Mengenauswahl beim Löschen
});

const confirmScanData = ref({
  artikel: null,
  menge: 1,
  requestData: null
});

const artikelAuswahl = ref({
  artikel: [],  // Mehrere Artikel zur Auswahl
  menge: 1,
  ean: ''
});

const removeQuantityData = ref({
  positionId: null,
  currentMenge: 0,
  removeMenge: 1,
  artikelInfo: null
});

const scanFeedback = ref({
  show: false,
  type: 'info',
  message: ''
});

// ========================================
// COMPUTED
// ========================================

// Primäre Rolle für UI-Anzeige (Priorisierung: refurbish > office > versand)
// Admins haben immer Zugriff als 'refurbish' Standardrolle
const userRole = computed(() => {
  if (authStore.user?.refurbish) return 'refurbish';
  if (authStore.user?.office) return 'office';
  if (authStore.user?.lagerist) return 'versand';
  // Admins/Super-Admins/Developer haben Zugriff mit Refurbish-Rechten
  if (authStore.user?.admin || authStore.user?.super_admin || authStore.user?.developer) return 'refurbish';
  return null;
});

// Alle Rollen des Benutzers
const userRoles = computed(() => {
  const roles = [];
  if (authStore.user?.refurbish) roles.push('refurbish');
  if (authStore.user?.office) roles.push('office');
  if (authStore.user?.lagerist) roles.push('versand');
  return roles;
});

// Hat Benutzer die Refurbish-Rolle?
const hasRefurbishRole = computed(() => authStore.user?.refurbish === true || authStore.user?.refurbish === 1 || authStore.user?.refurbish === '1');

// Gesamtstückzahl berechnen (Summe aller Mengen)
const gesamtStueckzahl = computed(() => {
  return positionen.value.reduce((sum, pos) => sum + (parseInt(pos.menge) || 0), 0);
});

const filteredPaletten = computed(() => {
  let filtered = paletten.value;

  // Tab-Filter: Aktiv vs Archiv
  if (viewTab.value === 'aktiv') {
    // Aktive Paletten: erfassung, office_freigabe, versand_final
    filtered = filtered.filter(p =>
      p.status === 'erfassung' ||
      p.status === 'office_freigabe' ||
      p.status === 'versand_final'
    );
  } else {
    // Archiv: abgeschlossen
    filtered = filtered.filter(p => p.status === 'abgeschlossen');
  }

  // Status-Filter
  if (statusFilter.value !== 'alle') {
    filtered = filtered.filter(p => p.status === statusFilter.value);
  }

  return filtered;
});

const palettenTableHeaders = computed(() => [
  { title: 'Paletten-Nr.', key: 'paletten_nr', sortable: true },
  { title: 'Arbeits-Nr.', key: 'arbeits_paletten_nr', sortable: true },
  { title: 'Erstellt von', key: 'erstellt_von_name', sortable: true },
  { title: 'Erstellt am', key: 'erfassung_datum', sortable: true },
  { title: 'Artikel', key: 'anzahl_positionen', sortable: true },
  { title: 'Stückzahl', key: 'gesamtstueckzahl', sortable: true },
  { title: 'Gewicht', key: 'gesamtgewicht', sortable: true },
  { title: 'Status', key: 'status', sortable: true },
  { title: 'Aktionen', key: 'actions', sortable: false }
]);

const positionenTableHeaders = computed(() => {
  const headers = [
    { title: '#', key: 'reihenfolge', width: '60px' },
    { title: 'EAN', key: 'gtin_ean', sortable: false },
    { title: 'CMMF Code', key: 'code_cmmf', sortable: false },
    { title: 'Beschreibung', key: 'description_cmmf', sortable: false },
    { title: 'Menge', key: 'menge', sortable: false, width: '80px' },
    { title: 'Gewicht', key: 'gewicht_kg', sortable: false, width: '120px' },
    { title: 'Gescannt', key: 'erfasst_datum', sortable: false }
  ];

  if (selectedPalette.value?.status === 'erfassung' && userRole.value === 'refurbish') {
    headers.push({ title: 'Aktionen', key: 'actions', sortable: false, width: '80px' });
  }

  return headers;
});

// ========================================
// METHODS - Daten laden
// ========================================

const aktiviereScanner = async () => {
  scannerAktiv.value = true;
  selectedPalette.value = null;
  await fetchOffenePaletten();
  await fetchArtikelListe();
  await nextTick();
  scannerInput.value?.focus();
};

const fetchArtikelListe = async () => {
  loading.value.artikel = true;
  try {
    const response = await axios.get(`${API_BASE_URL}refurbish/index.php`, {
      params: { action: 'get_artikel_katalog' },
      withCredentials: true
    });

    if (response.data.status === 'success') {
      artikelListe.value = response.data.artikel || [];
    }
  } catch (error) {
    console.error('Fehler beim Laden der Artikel-Liste:', error);
  } finally {
    loading.value.artikel = false;
  }
};

const deaktiviereScanner = async () => {
  scannerAktiv.value = false;
  scanInput.value = '';
  scanMenge.value = 1;
  // Paletten-Liste neu laden, damit offene Paletten sichtbar sind
  await fetchPaletten();
  await fetchStatistik();
};

const fetchOffenePaletten = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}refurbish/index.php`, {
      params: { action: 'get_offene_paletten' },
      withCredentials: true
    });

    if (response.data.status === 'success') {
      offenePaletten.value = response.data.paletten;
    }
  } catch (error) {
    console.error('Fehler beim Laden der offenen Paletten:', error);
  }
};

const fetchPaletten = async () => {
  loading.value.paletten = true;
  try {
    const response = await axios.get(`${API_BASE_URL}refurbish/index.php`, {
      params: { action: 'get_paletten' },
      withCredentials: true
    });

    if (response.data.status === 'success') {
      paletten.value = response.data.paletten;
    } else {
      toast.error(response.data.message || 'Fehler beim Laden der Paletten');
    }
  } catch (error) {
    console.error('Fehler beim Laden der Paletten:', error);
    toast.error('Netzwerkfehler beim Laden der Paletten');
  } finally {
    loading.value.paletten = false;
  }
};

const fetchStatistik = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}refurbish/index.php`, {
      params: { action: 'get_statistik' },
      withCredentials: true
    });

    if (response.data.status === 'success') {
      statistik.value = response.data.statistik;
    }
  } catch (error) {
    console.error('Fehler beim Laden der Statistik:', error);
  }
};

const loadPalette = async (paletteId) => {
  loading.value.positionen = true;
  try {
    const response = await axios.get(`${API_BASE_URL}refurbish/index.php`, {
      params: { action: 'get_palette', palette_id: paletteId },
      withCredentials: true
    });

    if (response.data.status === 'success') {
      selectedPalette.value = response.data.palette;
      positionen.value = response.data.palette.positionen || [];

      // Scanner fokussieren nach kurzer Verzögerung
      if (selectedPalette.value.status === 'erfassung' && userRole.value === 'refurbish') {
        await nextTick();
        setTimeout(() => {
          scannerInput.value?.focus();
        }, 300);
      }
    } else {
      toast.error(response.data.message || 'Fehler beim Laden der Palette');
    }
  } catch (error) {
    console.error('Fehler beim Laden der Palette:', error);
    toast.error('Netzwerkfehler beim Laden der Palette');
  } finally {
    loading.value.positionen = false;
  }
};

const backToList = () => {
  selectedPalette.value = null;
  positionen.value = [];
  fetchPaletten();
  fetchStatistik();
  // Offene Paletten aktualisieren, falls Scanner aktiv ist
  if (scannerAktiv.value) {
    fetchOffenePaletten();
  }
};

// ========================================
// METHODS - Scanner
// ========================================

// EAN-Code normalisieren (führende Nullen ergänzen wenn Scanner sie abgeschnitten hat)
const normalizeEAN = (ean) => {
  const trimmed = ean.trim();
  const length = trimmed.length;

  // Nur numerische Eingaben normalisieren
  if (!/^\d+$/.test(trimmed)) {
    return trimmed;
  }

  // Wenn 7 Stellen → EAN-8 mit führender 0
  // Scanner hat eine 0 abgeschnitten: 1234567 → 01234567
  if (length === 7) {
    return '0' + trimmed;
  }

  // Wenn 12 Stellen → EAN-13 mit führender 0
  // Scanner hat eine 0 abgeschnitten: 012345678901 → 0012345678901
  if (length === 12) {
    return '0' + trimmed;
  }

  // Alle anderen Längen (8, 13, etc.) unverändert zurückgeben
  return trimmed;
};

const autoScanOnEanComplete = () => {
  // Auto-Scan wenn EAN vollständig ist (7-13 Stellen für verschiedene EAN-Formate)
  const input = scanInput.value?.trim() || '';
  const length = input.length;

  // EAN-7/8 oder EAN-12/13 akzeptieren (Scanner kann führende Nullen abschneiden)
  if (length >= 7 && length <= 13 && /^\d+$/.test(input)) {
    // Nur Zahlen und richtige Länge - automatisch scannen
    setTimeout(() => {
      handleScan();
    }, 100);
  }
};

const handleScan = async () => {
  let input = scanInput.value.trim();

  if (!input) {
    const msg = searchType.value === 'ean' ? 'Bitte EAN eingeben oder scannen'
             : searchType.value === 'model' ? 'Bitte Code Commercial eingeben'
             : 'Bitte CMMF-Nummer eingeben';
    showScanFeedback('error', msg);
    return;
  }

  // EAN normalisieren (führende Nullen ergänzen, falls Scanner sie abgeschnitten hat)
  if (searchType.value === 'ean') {
    input = normalizeEAN(input);
  }

  loading.value.scanning = true;

  try {
    // Erst Artikel-Infos vom Katalog holen
    const params = {
      action: 'get_artikel_info'
    };

    if (searchType.value === 'ean') {
      params.ean = input;
    } else if (searchType.value === 'model') {
      params.code_commercial = input;
    } else if (searchType.value === 'cmmf') {
      params.code_cmmf = input;
    }

    const artikelResponse = await axios.get(
      `${API_BASE_URL}refurbish/index.php`,
      { params, withCredentials: true }
    );

    if (artikelResponse.data.status === 'success') {
      // Artikel gefunden - Bestätigungsdialog anzeigen
      const artikel = artikelResponse.data.artikel;
      confirmScanData.value = {
        artikel: artikel,
        menge: scanMenge.value || 1,
        requestData: {
          action: 'scan_article',
          menge: scanMenge.value || 1,
          ...(searchType.value === 'ean' ? { ean: input }
             : searchType.value === 'model' ? { code_commercial: input }
             : { code_cmmf: input })
        }
      };
      dialogs.value.confirmScan = true;
      loading.value.scanning = false;
    } else if (artikelResponse.data.status === 'multiple') {
      // Mehrere Artikel mit gleichem EAN - Auswahl anzeigen
      artikelAuswahl.value = {
        artikel: artikelResponse.data.artikel,
        menge: scanMenge.value || 1,
        ean: input
      };
      dialogs.value.selectArtikel = true;
      loading.value.scanning = false;
    } else {
      showScanFeedback('error', artikelResponse.data.message || 'Artikel nicht im Katalog gefunden');
      loading.value.scanning = false;
    }

  } catch (error) {
    console.error('Scan-Fehler:', error);
    // Unterscheiden zwischen API-Fehler und Netzwerkfehler
    if (error.response?.data?.message) {
      showScanFeedback('error', error.response.data.message);
    } else if (error.response) {
      showScanFeedback('error', 'Artikel nicht gefunden');
    } else {
      showScanFeedback('error', 'Netzwerkfehler beim Scannen');
    }
    loading.value.scanning = false;
  }
};

const confirmAndScan = async () => {
  if (!confirmScanData.value.requestData) return;

  loading.value.scanning = true;
  dialogs.value.confirmScan = false;

  try {
    const response = await axios.post(
      `${API_BASE_URL}refurbish/index.php`,
      confirmScanData.value.requestData,
      { withCredentials: true }
    );

    if (response.data.status === 'success') {
      const { position, palette, paletten_nr, action } = response.data;
      const ean = position.gtin_ean || scanInput.value;

      await fetchOffenePaletten();

      // Wenn eine Palette geöffnet ist, diese neu laden
      if (selectedPalette.value && selectedPalette.value.id) {
        await loadPalette(selectedPalette.value.id);
      }

      if (action === 'updated') {
        showScanFeedback('success', `Menge erhöht: ${position.description_cmmf || ean} (${position.menge}x) → Palette ${paletten_nr}`);
      } else {
        showScanFeedback('success', `Artikel hinzugefügt: ${position.description_cmmf || ean} → Palette ${paletten_nr}`);
      }

      scanInput.value = '';
      scanMenge.value = 1;
      await fetchStatistik();

    } else if (response.data.status === 'warning') {
      showScanFeedback('warning', response.data.message);
      scanInput.value = '';
    } else {
      showScanFeedback('error', response.data.message || 'Fehler beim Scannen');
    }

  } catch (error) {
    console.error('Scan-Fehler:', error);
    // Detaillierte Fehlermeldung anzeigen
    if (error.response?.data?.message) {
      showScanFeedback('error', error.response.data.message);
    } else if (error.response) {
      showScanFeedback('error', 'Fehler beim Scannen des Artikels');
    } else {
      showScanFeedback('error', 'Netzwerkfehler beim Scannen');
    }
  } finally {
    loading.value.scanning = false;
    await nextTick();
    scannerInput.value?.focus();
  }
};

const selectArtikelFromList = (artikel) => {
  // Artikel aus der Auswahl-Liste wurde gewählt
  dialogs.value.selectArtikel = false;

  confirmScanData.value = {
    artikel: artikel,
    menge: artikelAuswahl.value.menge,
    requestData: {
      action: 'scan_article',
      menge: artikelAuswahl.value.menge,
      ean: artikelAuswahl.value.ean,
      code_cmmf: artikel.code_cmmf  // Spezifische CMMF zur Eindeutigkeit
    }
  };

  dialogs.value.confirmScan = true;
};

const cancelScan = () => {
  dialogs.value.confirmScan = false;
  scanInput.value = '';
  scanMenge.value = 1;
  nextTick(() => {
    scannerInput.value?.focus();
  });
};

const showScanFeedback = (type, message) => {
  scanFeedback.value = {
    show: true,
    type: type,
    message: message
  };

  // Auto-Hide nach 3 Sekunden (außer bei Fehler)
  if (type !== 'error') {
    setTimeout(() => {
      scanFeedback.value.show = false;
    }, 3000);
  }
};

const removePosition = async (positionId) => {
  // Position finden
  const position = positionen.value.find(p => p.id === positionId);
  if (!position) {
    toast.error('Position nicht gefunden');
    return;
  }

  // Wenn Menge > 1, Dialog zur Auswahl zeigen
  if (position.menge > 1) {
    removeQuantityData.value = {
      positionId: positionId,
      currentMenge: position.menge,
      removeMenge: 1,
      artikelInfo: {
        code_cmmf: position.code_cmmf,
        description: position.description_cmmf
      }
    };
    dialogs.value.removeQuantity = true;
    return;
  }

  // Menge = 1, direkt löschen
  if (!confirm('Möchten Sie diesen Artikel wirklich entfernen?')) return;
  await executeRemovePosition(positionId, position.menge);
};

const executeRemovePosition = async (positionId, menge) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}refurbish/index.php`,
      {
        action: 'remove_position',
        position_id: positionId,
        menge: menge  // Anzahl die entfernt werden soll
      },
      { withCredentials: true }
    );

    if (response.data.status === 'success') {
      if (response.data.palette_deleted) {
        // Palette wurde komplett gelöscht
        selectedPalette.value = null;
        positionen.value = [];
        toast.success('Letzte Position entfernt - Palette wurde automatisch gelöscht');
      } else if (response.data.deleted) {
        // Position komplett gelöscht
        positionen.value = positionen.value.filter(p => p.id !== positionId);
        selectedPalette.value.anzahl_positionen = positionen.value.length;
      } else {
        // Menge reduziert
        const pos = positionen.value.find(p => p.id === positionId);
        if (pos) {
          pos.menge = response.data.neue_menge;
        }
      }

      if (!response.data.palette_deleted) {
        toast.success(response.data.message || 'Artikel entfernt');
      }

      // Sidebar und Statistik aktualisieren
      await fetchOffenePaletten();
      await fetchStatistik();
    } else {
      toast.error(response.data.message || 'Fehler beim Entfernen');
    }
  } catch (error) {
    console.error('Fehler beim Entfernen:', error);
    toast.error('Netzwerkfehler');
  }
};

const confirmRemoveQuantity = async () => {
  if (removeQuantityData.value.removeMenge < 1 ||
      removeQuantityData.value.removeMenge > removeQuantityData.value.currentMenge) {
    toast.error('Ungültige Menge');
    return;
  }

  dialogs.value.removeQuantity = false;
  await executeRemovePosition(
    removeQuantityData.value.positionId,
    removeQuantityData.value.removeMenge
  );
};

// ========================================
// METHODS - Status & Workflow
// ========================================

const confirmFreigabeOffice = () => {
  if (positionen.value.length === 0) {
    toast.warning('Bitte scannen Sie zuerst Artikel');
    return;
  }
  dialogs.value.freigabeOffice = true;
};

const freigabeAnOffice = async () => {
  await updateStatus('office_freigabe');
  dialogs.value.freigabeOffice = false;
};

const updateStatus = async (newStatus) => {
  loading.value.status = true;

  try {
    const response = await axios.post(
      `${API_BASE_URL}refurbish/index.php`,
      {
        action: 'update_palette_status',
        palette_id: selectedPalette.value.id,
        status: newStatus,
        office_notiz: selectedPalette.value.office_notiz,
        lager_notiz: selectedPalette.value.lager_notiz
      },
      { withCredentials: true }
    );

    if (response.data.status === 'success') {
      selectedPalette.value.status = newStatus;
      toast.success('Status erfolgreich aktualisiert');

      // Offene Paletten-Liste aktualisieren (Sidebar im Scanner-Modus)
      if (scannerAktiv.value) {
        await fetchOffenePaletten();
      }

      // Zurück zur Liste nach Freigabe
      if (newStatus === 'office_freigabe' || newStatus === 'versand_final' || newStatus === 'abgeschlossen') {
        setTimeout(() => {
          backToList();
        }, 1000);
      }
    } else {
      toast.error(response.data.message || 'Fehler beim Aktualisieren');
    }
  } catch (error) {
    console.error('Fehler beim Status-Update:', error);
    toast.error('Netzwerkfehler');
  } finally {
    loading.value.status = false;
  }
};

const saveNote = async (noteField) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}refurbish/index.php`,
      {
        action: 'add_note',
        palette_id: selectedPalette.value.id,
        note_field: noteField,
        notiz: selectedPalette.value[noteField]
      },
      { withCredentials: true }
    );

    if (response.data.status === 'success') {
      toast.success('Notiz gespeichert');
    }
  } catch (error) {
    console.error('Fehler beim Speichern der Notiz:', error);
  }
};

// ========================================
// METHODS - Hilfsfunktionen
// ========================================

const formatDateTime = (dateTime) => {
  if (!dateTime) return '—';
  const date = new Date(dateTime);
  return date.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatTime = (dateTime) => {
  if (!dateTime) return '—';
  const date = new Date(dateTime);
  return date.toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit'
  });
};

const getTimeAgo = (dateTime) => {
  if (!dateTime) return 'unbekannt';
  const now = new Date();
  const past = new Date(dateTime);
  const diffMs = now - past;
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return 'gerade eben';
  if (diffMins < 60) return `${diffMins} Minute${diffMins > 1 ? 'n' : ''}`;

  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours} Stunde${diffHours > 1 ? 'n' : ''}`;

  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays} Tag${diffDays > 1 ? 'en' : ''}`;
};

const getStatusColor = (status) => {
  switch (status) {
    case 'erfassung': return 'orange';
    case 'office_freigabe': return 'indigo';
    case 'versand_final': return 'blue';
    case 'abgeschlossen': return 'green';
    default: return 'grey';
  }
};

const getStatusIcon = (status) => {
  switch (status) {
    case 'erfassung': return 'mdi-package-variant';
    case 'office_freigabe': return 'mdi-briefcase-check';
    case 'lager_final': return 'mdi-warehouse';
    case 'abgeschlossen': return 'mdi-check-circle';
    default: return 'mdi-help-circle';
  }
};

const getStatusText = (status, palette = null) => {
  switch (status) {
    case 'erfassung':
      // Unterscheidung: Neue Palette vs. Nachbearbeitung
      // Nachbearbeitung = Office hat bereits daran gearbeitet
      if (palette && palette.bearbeitet_von_office_user_id) {
        return 'Nachbearbeitung';
      }
      return 'Wird erstellt';
    case 'office_freigabe': return 'Office Freigabe';
    case 'versand_final': return 'Versand Final';
    case 'abgeschlossen': return 'Abgeschlossen';
    default: return status;
  }
};

// ========================================
// LIFECYCLE
// ========================================

onMounted(() => {
  fetchPaletten();
  fetchStatistik();
});

// Scanner Focus Watch
watch(() => selectedPalette.value?.status, async (newStatus) => {
  if (newStatus === 'erfassung' && userRole.value === 'refurbish') {
    await nextTick();
    setTimeout(() => {
      scannerInput.value?.focus();
    }, 300);
  }
});
</script>

<style scoped>
.refurbish-container {
  padding: 20px;
}

.status-overview-card {
  background-color: rgba(30, 30, 35, 0.7);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition: all 0.3s ease;
}

.card-divider {
  opacity: 0.1;
}

.form-field {
  margin-bottom: 12px;
}

.form-field :deep(.v-field__input) {
  color: rgba(255, 255, 255, 0.85);
}

.form-field :deep(.v-field__outline) {
  color: rgba(255, 255, 255, 0.12);
}

.form-field :deep(.v-field:hover .v-field__outline) {
  color: rgba(255, 255, 255, 0.18);
}

.overview-card,
.palette-info-card,
.positions-card,
.actions-card {
  background: rgba(30, 30, 40, 0.8);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.info-box {
  padding: 12px;
  background: rgba(50, 50, 60, 0.5);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.info-label {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.6);
  font-weight: 500;
  margin-bottom: 4px;
}

.info-value {
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 600;
}

.info-subtext {
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.4);
  margin-top: 2px;
}

.checklist {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.checklist-item {
  display: flex;
  align-items: center;
  padding: 8px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 4px;
}

code {
  background: rgba(255, 255, 255, 0.1);
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.85rem;
}
</style>
